import { useState, type ReactElement } from "react";

import { ApiError } from "../../api/client";
import type { AdminSubstudy } from "../../api/types";
import { TabPanel } from "../Tabs";
import { downloadConsentsZip, downloadCsv, downloadPhotosZip } from "./download";

// One-click CSV/ZIP exports over the existing audit-logged admin endpoints:
// platform-wide buttons up top, then one row of exports per substudy.
export function ExportsTab({ substudies }: { substudies: AdminSubstudy[] }): ReactElement {
  const [downloadError, setDownloadError] = useState<string | null>(null);

  async function run(download: () => Promise<void>): Promise<void> {
    setDownloadError(null);
    try {
      await download();
    } catch (err) {
      setDownloadError(err instanceof ApiError ? err.message : "Download failed.");
    }
  }

  return (
    <TabPanel id="exports" title="Exports">
      <p className="muted">
        Downloads are audit-logged. The analysis export contains direct identifiers; treat the
        file as PHI.
      </p>
      <p>
        <button
          className="btn-primary-glow"
          onClick={() => void run(() => downloadCsv("/admin/export/analysis.csv", "faceage-analysis.csv"))}
        >
          Analysis export (CSV)
        </button>{" "}
        <button className="btn-ghost" onClick={() => void run(() => downloadPhotosZip())}>
          Photo export (ZIP)
        </button>{" "}
        <button className="btn-ghost" onClick={() => void run(() => downloadConsentsZip())}>
          Consents ZIP (all studies)
        </button>
      </p>
      <h3>Per-Substudy Exports</h3>
      {substudies.length === 0 ? (
        <p className="muted">No substudies yet.</p>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Substudy</th>
              <th>Slug</th>
              <th>Exports</th>
            </tr>
          </thead>
          <tbody>
            {substudies.map((t) => (
              <tr key={t.id}>
                <td>{t.name}</td>
                <td>{t.slug}</td>
                <td>
                  <button
                    className="btn-ghost btn-sm"
                    onClick={() =>
                      void run(() =>
                        downloadCsv(`/admin/substudies/${t.slug}/results.csv`, `${t.slug}-results.csv`),
                      )
                    }
                  >
                    Results CSV
                  </button>{" "}
                  <button
                    className="btn-ghost btn-sm"
                    onClick={() =>
                      void run(() =>
                        downloadCsv(
                          `/admin/export/substudy/${t.slug}.csv`,
                          `${t.slug}-questionnaires.csv`,
                        ),
                      )
                    }
                  >
                    Questionnaires CSV
                  </button>{" "}
                  <button
                    className="btn-ghost btn-sm"
                    onClick={() => void run(() => downloadConsentsZip(t.slug))}
                  >
                    Consents ZIP
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {downloadError ? <p className="form-error">{downloadError}</p> : null}
    </TabPanel>
  );
}
